// Shared doctor profile card (THU-358). One component, two homes: the /doctors/:id page body and the
// booking Step-2 modal. Renders the full public profile (photo, name, specialty, rating, years, bio,
// focus areas, education, languages) + a favorite heart toggle. Non-clinical identity only. Ported
// from Luan's consumer mockup (.dp-* classes). The `footer` slot carries the caller's action(s) —
// "Choose Dr. X" in booking, Book/Message links on the page.
import { useEffect, useState, type ReactNode } from "react";
import { Avatar, Modal } from "@championcare/ui";
import { useDoctorsApi, type DoctorProfile } from "../lib/doctorsApi";
import { useAsync } from "../lib/useAsync";
import { Loading, ErrorState } from "./ui";

const ClockIcon = (
  <svg viewBox="0 0 24 24" className="clock" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);
const HeartIcon = ({ filled }: { filled: boolean }) => (
  <svg viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

function doctorName(d: { first_name?: string | null; last_name?: string | null }): string {
  const name = [d.first_name, d.last_name].map((s) => (s ?? "").trim()).filter(Boolean).join(" ");
  return name ? `Dr. ${name}` : "Your doctor";
}

export function DoctorCard({
  doctor,
  onFavoriteChange,
  footer,
}: {
  doctor: DoctorProfile;
  onFavoriteChange?: (next: boolean) => void;
  footer?: ReactNode;
}): JSX.Element {
  const doctorsApi = useDoctorsApi();
  const [fav, setFav] = useState(doctor.is_favorite);
  const [busy, setBusy] = useState(false);
  const initials = `${doctor.first_name.charAt(0)}${doctor.last_name.charAt(0)}`.toUpperCase();

  // Keep the heart in sync if the doctor (or its favorite state) changes under us — e.g. the booking
  // list reloads after a toggle, or a different doctor's card is opened in the same modal instance.
  useEffect(() => {
    setFav(doctor.is_favorite);
  }, [doctor.id, doctor.is_favorite]);

  async function toggleFavorite() {
    if (busy) return;
    const next = !fav;
    setFav(next); // optimistic
    setBusy(true);
    try {
      if (next) await doctorsApi.favorite(doctor.id);
      else await doctorsApi.unfavorite(doctor.id);
      onFavoriteChange?.(next);
    } catch {
      setFav(!next); // revert on failure
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dp-card">
      <div className="dp-header">
        <Avatar className="dp-photo" src={doctor.avatar_url} initials={initials} />
        <div className="dp-name">{doctorName(doctor)}</div>
        {doctor.specialty && <div className="dp-spec">{doctor.specialty}</div>}
        <button
          type="button"
          className={`dp-favorite ${fav ? "active" : ""}`}
          onClick={toggleFavorite}
          disabled={busy}
          aria-pressed={fav}
        >
          <HeartIcon filled={fav} />
          {fav ? "Favorited" : "Favorite"}
        </button>
      </div>

      {doctor.years_experience != null && (
        <div className="dp-stats">
          <span className="dp-stat">
            {ClockIcon}
            {doctor.years_experience} years experience
          </span>
        </div>
      )}

      {doctor.bio && <p className="dp-bio">{doctor.bio}</p>}

      {doctor.focus_areas.length > 0 && (
        <div className="dp-section">
          <div className="dp-section-label">Focus areas</div>
          <div className="dp-tags">
            {doctor.focus_areas.map((f) => (
              <span key={f} className="dp-tag">{f}</span>
            ))}
          </div>
        </div>
      )}

      {doctor.education && (
        <div className="dp-section">
          <div className="dp-section-label">Education</div>
          <div className="dp-section-content">{doctor.education}</div>
        </div>
      )}

      {doctor.languages.length > 0 && (
        <div className="dp-section">
          <div className="dp-section-label">Languages</div>
          <div className="dp-section-content">{doctor.languages.join(" · ")}</div>
        </div>
      )}

      {footer && <div className="dp-footer">{footer}</div>}
    </div>
  );
}

// Fetch-by-id wrapper: opens the shared card in a Modal given just a doctor id (used by surfaces that
// only carry the id, e.g. the Messages chat header). Booking passes a full profile straight to
// <DoctorCard> instead, so it needs no fetch.
export function DoctorCardModal({
  doctorId,
  onClose,
}: {
  doctorId: string;
  onClose: () => void;
}): JSX.Element {
  const doctorsApi = useDoctorsApi();
  const { data, loading, error } = useAsync(() => doctorsApi.get(doctorId), [doctorId]);
  return (
    <Modal open onClose={onClose} wide>
      {loading ? (
        <Loading />
      ) : error || !data ? (
        <ErrorState message={error ?? "We couldn’t load that doctor."} />
      ) : (
        <DoctorCard doctor={data} />
      )}
    </Modal>
  );
}
